/**
 * MicRecorder — captures mic audio in the browser and POSTs it to
 * `/api/stt/transcribe`, returning the transcript via callback.
 *
 * Two UX modes:
 *  • Click-to-talk: parent renders this with no `autoListenSignal`. The
 *    user clicks to start recording, clicks again to stop and send.
 *  • Hands-free: parent bumps the integer `autoListenSignal` prop every
 *    time the tutor finishes speaking. The component then auto-starts a
 *    recording, monitors mic RMS, and auto-stops on a sustained silence
 *    window (~1.2 s), at which point it transcribes and calls
 *    `onTranscript(text, { autoSend: true })`.
 *
 * Visual: brand-gold mic button (matches the TTS toggle) with three
 * states — idle, recording (red glow), uploading (spinner).
 *
 * Permissions: the component never asks for the mic until the user
 * clicks the button (or the parent flips hands-free ON); the browser
 * remembers consent after the first grant.
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { api } from '../lib/apiClient';

const GOLD = 'var(--gr-gold, #FCD359)';
const GOLD_DARK = 'var(--gr-gold-dark, #E0B73D)';
const GOLD_DEEP = 'var(--gr-gold-deep, #8C6C1F)';

// Silence-detection knobs for hands-free mode.
const HF_SILENCE_RMS = 0.012;       // mic samples below this count as silent
const HF_SILENCE_MS = 1200;          // sustained silence before auto-stop
const HF_MIN_SPEECH_MS = 600;        // require at least this much voiced audio
const HF_MAX_CLIP_MS = 30000;        // hard cap so a rogue ambient mic doesn't run forever

let __healthPromise = null;
async function sttReady() {
  if (!__healthPromise) {
    __healthPromise = api.get('/api/stt/health')
      .then((r) => !!r?.data?.ready)
      .catch(() => false);
  }
  return __healthPromise;
}

const MicRecorder = ({
  onTranscript,           // (text, { autoSend }) => void
  disabled = false,
  autoListenSignal = 0,   // integer; incrementing it kicks off hands-free capture
  testId = 'mic-record-btn',
}) => {
  const [ready, setReady] = useState(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [level, setLevel] = useState(0); // live mic RMS, 0..1, drives the VU bar

  const mediaRecRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const handsFreeRef = useRef(false);
  const audioCtxRef = useRef(null);
  const rmsRafRef = useRef(null);
  const lastVoicedAtRef = useRef(0);
  const startedAtRef = useRef(0);
  const voicedDurationRef = useRef(0);
  // We throttle setState updates to roughly the next animation frame
  // boundary so a long recording session doesn't trigger 60+ rerenders
  // per second of the parent ChatPanel.
  const lastLevelEmitRef = useRef(0);

  useEffect(() => { sttReady().then(setReady); }, []);

  // Tear-down helper used by both modes.
  const teardown = useCallback(() => {
    try {
      if (rmsRafRef.current) cancelAnimationFrame(rmsRafRef.current);
      rmsRafRef.current = null;
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
        try { mediaRecRef.current.stop(); } catch { /* ignore */ }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } catch { /* ignore */ }
  }, []);

  const uploadAndTranscribe = useCallback(async (blob, opts) => {
    if (!blob || blob.size < 800) {
      // Too short — likely an accidental tap. Stay silent.
      return;
    }
    setUploading(true);
    setErr('');
    try {
      const form = new FormData();
      form.append('audio', blob, 'speech.webm');
      const { data } = await api.post('/api/stt/transcribe', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const text = (data?.text || '').trim();
      if (text && typeof onTranscript === 'function') {
        // Low-confidence clips are NEVER auto-sent — even in hands-free
        // mode the student should review/edit before /api/chat fires.
        const wantedAutoSend = !!opts?.autoSend;
        const autoSend = wantedAutoSend && !data?.low_conf;
        onTranscript(text, {
          autoSend,
          confidence: typeof data?.confidence === 'number' ? data.confidence : null,
          lowConf: !!data?.low_conf,
          // Caller can show a "we heard X — please confirm" affordance
          // when handsFree was requested but we suppressed auto-send.
          autoSendSuppressed: wantedAutoSend && !autoSend,
        });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Transcription failed');
    } finally {
      setUploading(false);
    }
  }, [onTranscript]);

  // Start a recording. `handsFree=true` engages RMS-based auto-stop and
  // marks the upload as `autoSend: true` so the parent fires `/api/chat`
  // without waiting for the user to hit Send.
  const startRecording = useCallback(async (handsFree) => {
    if (!ready || recording || uploading || disabled) return;
    setErr('');
    handsFreeRef.current = !!handsFree;
    voicedDurationRef.current = 0;
    startedAtRef.current = performance.now();
    lastVoicedAtRef.current = startedAtRef.current;

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setErr(e.message || 'Microphone permission denied');
      return;
    }
    streamRef.current = stream;
    chunksRef.current = [];

    // Pick the best supported mime — Chrome/Safari prefer audio/webm.
    let mime = '';
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mime = 'audio/webm;codecs=opus';
      else if (MediaRecorder.isTypeSupported('audio/webm')) mime = 'audio/webm';
      else if (MediaRecorder.isTypeSupported('audio/mp4')) mime = 'audio/mp4';
    }
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    mediaRecRef.current = rec;

    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
      const wasHandsFree = handsFreeRef.current;
      teardown();
      setRecording(false);
      setLevel(0);
      // Discard hands-free clips that never registered enough actual voicing —
      // prevents Whisper hallucinations on silence/ambient noise.
      if (wasHandsFree && voicedDurationRef.current < HF_MIN_SPEECH_MS) {
        return;
      }
      await uploadAndTranscribe(blob, { autoSend: wasHandsFree });
    };
    rec.start();
    setRecording(true);

    // Web-Audio analyser runs for EVERY recording (click-to-talk or
    // hands-free). The same RMS reading powers:
    //   • the live VU meter inside the mic button (visual feedback —
    //     students see their voice is being captured)
    //   • the silence-detection auto-stop (hands-free only)
    try {
      // eslint-disable-next-line new-cap
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);

      const tick = () => {
        if (!audioCtxRef.current) return;
        analyser.getFloatTimeDomainData(buf);
        // Compute RMS — cheap, robust, no smoothing needed for our purpose.
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        const now = performance.now();

        // ── VU meter feed ─────────────────────────────────────────────
        // Map RMS (0..~0.3 for normal speech) onto a 0..1 display range
        // with a soft compression so soft voices still register, and
        // throttle the React state update to ~30 fps.
        if (now - lastLevelEmitRef.current > 33) {
          lastLevelEmitRef.current = now;
          // Square root gives a perceptually smoother bar (audio is loud).
          const display = Math.min(1, Math.sqrt(rms / 0.15));
          setLevel(display);
        }

        // ── Silence detection — only when hands-free ──────────────────
        if (handsFreeRef.current) {
          if (rms > HF_SILENCE_RMS) {
            voicedDurationRef.current += (now - lastVoicedAtRef.current);
            lastVoicedAtRef.current = now;
          }
          const elapsed = now - startedAtRef.current;
          const silenceWindow = now - lastVoicedAtRef.current;
          const enoughSpeech = voicedDurationRef.current >= HF_MIN_SPEECH_MS;
          if ((enoughSpeech && silenceWindow >= HF_SILENCE_MS) || elapsed >= HF_MAX_CLIP_MS) {
            try { rec.stop(); } catch { /* ignore */ }
            return; // onstop will fire teardown + upload
          }
        }
        rmsRafRef.current = requestAnimationFrame(tick);
      };
      rmsRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      // Web Audio failed — VU meter just stays at zero. For hands-free
      // we additionally need a hard 6-second cap so we don't trap the
      // user with no way to stop.
      if (handsFree) {
        setTimeout(() => { try { rec.stop(); } catch { /* ignore */ } }, 6000);
      }
    }
  }, [ready, recording, uploading, disabled, teardown, uploadAndTranscribe]);

  const stopRecording = useCallback(() => {
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      try { mediaRecRef.current.stop(); } catch { /* ignore */ }
    }
  }, []);

  // Parent signals hands-free capture by incrementing autoListenSignal.
  // Defer the call so React's "set-state-in-effect" rule is satisfied and
  // the toggle state has fully committed before we touch the mic.
  useEffect(() => {
    if (!autoListenSignal) return;
    if (!ready) return;
    if (recording || uploading || disabled) return;
    const t = setTimeout(() => startRecording(true), 0);
    return () => clearTimeout(t);
    // We intentionally don't include startRecording in deps — the signal
    // increment is the trigger; including the callback would re-fire on
    // every ref update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoListenSignal, ready]);

  // Cleanup if the component unmounts mid-recording.
  useEffect(() => () => teardown(), [teardown]);

  const onClick = useCallback(() => {
    if (recording) stopRecording();
    else startRecording(false);
  }, [recording, startRecording, stopRecording]);

  if (ready === false) return null;

  const tip = err
    ? `Mic error: ${err}`
    : uploading
      ? 'Transcribing…'
      : recording
        ? 'Recording — click to stop & send'
        : 'Click to speak your reply';

  // Same elongated-square family as the TTS toggle, but icon-only so the
  // input row doesn't get crowded.
  const bg = recording ? '#dc2626' : uploading ? '#f3f4f6' : GOLD;
  const fg = recording ? '#fff' : uploading ? '#374151' : GOLD_DEEP;
  const border = recording ? '#991b1b' : uploading ? 'rgba(0,0,0,0.12)' : GOLD_DARK;
  const shadow = recording
    ? '0 0 0 3px rgba(220,38,38,0.35)'
    : `0 1px 2px rgba(0,0,0,0.08), 0 0 0 1px ${GOLD_DARK}`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!ready || uploading || disabled}
      title={tip}
      aria-label={tip}
      aria-pressed={recording}
      data-testid={testId}
      className="inline-flex items-center justify-center self-stretch transition-all"
      style={{
        minWidth: 44,
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 6,
        background: bg,
        color: fg,
        border: `1px solid ${border}`,
        cursor: ready ? 'pointer' : 'not-allowed',
        boxShadow: shadow,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Live VU meter — only while recording. Fills the button from the
          bottom proportional to the current mic RMS; sits behind the
          stop-square glyph so the user sees both at once. */}
      {recording && (
        <span
          aria-hidden="true"
          data-testid="mic-vu-meter"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: `${Math.round(level * 100)}%`,
            background: 'rgba(255,255,255,0.28)',
            transition: 'height 60ms linear',
            pointerEvents: 'none',
          }}
        />
      )}
      <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center' }}>
        {uploading ? (
          <span style={{ animation: 'spin 1s linear infinite', fontSize: 16 }}>⋯</span>
        ) : recording ? (
          // Solid white square = "stop" (standard media control)
          <span style={{ width: 10, height: 10, background: '#fff', display: 'inline-block', borderRadius: 1 }} />
        ) : (
          // Mic glyph — keep it readable at 18px
          <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden="true">🎤</span>
        )}
      </span>
    </button>
  );
};

export default MicRecorder;
